import { InjectionToken, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule, BrowserTransferStateModule, makeStateKey, TransferState } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { CONFIG, ENVIRONMENT } from '@nx-averycom/environment';
import { config, environment } from '../environments/environment';
import { BrowserStateInterceptor } from '@nx-averycom/shared/utils';
import { InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

const APOLLO_CACHE = new InjectionToken<InMemoryCache>('apollo-cache');
const STATE_KEY = makeStateKey<any>('apollo.state');

@NgModule({
  declarations: [AppComponent],
  imports: [
    ApolloModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    BrowserTransferStateModule,
    RouterModule.forRoot([
      {
        path: 'interstitial',
        loadChildren: () =>
          import('@nx-averycom/interstitial/main-page').then(
            (module) => module.InterstitialMainPageModule
          ),
      },
      {
        path: 'templates',
        loadChildren: () => import('@nx-averycom/templates-lib').then((module) => module.TemplatesLibModule)
      },
      {
        path: 'industrial/templates',
        loadChildren: () => import('@nx-averycom/templates-lib').then((module) => module.TemplatesLibModule)
      },
      {
        path: 'predesign',
        data: { predesign: 'diy' },
        loadChildren: () =>
          import('@nx-averycom/predesign-lib').then(
            (module) => module.PredesignModule
          ),
      },
      {
        path: 'custom-printing/predesign',
        data: { predesign: 'dify' },
        loadChildren: () =>
          import('@nx-averycom/predesign-lib').then(
            (module) => module.PredesignModule
          ),
      },
    ], {initialNavigation: 'enabledBlocking'}),
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !environment.production, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
    }),
    EffectsModule.forRoot(),
  ],
  providers: [
    {provide: ENVIRONMENT, useValue: environment},
    {provide: CONFIG, useValue: config},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BrowserStateInterceptor,
      multi: true,
    },
    {
      provide: APOLLO_CACHE,
      useValue: new InMemoryCache(),
    },
    {
      provide: APOLLO_OPTIONS,
      useFactory(httpLink: HttpLink, cache: InMemoryCache, transferState: TransferState) {
        const isBrowser = transferState.hasKey<any>(STATE_KEY);
        if (isBrowser) {
          const state = transferState.get<any>(STATE_KEY, null);
          cache.restore(state);
        } else {
          transferState.onSerialize(STATE_KEY, () => {
            return cache.extract();
          });
          // Reset cache after extraction to avoid sharing between requests
          cache.reset();
        }

        return {
          link: httpLink.create({ uri:`${environment.domain}/api/v1/graphql` }),
          cache,
          ...(isBrowser
            ? {
              ssrForceFetchDelay: 100,
            }
            :
            {
              ssrMode: true
            }),
          connectToDevTools: true,
        };
      },
      deps: [HttpLink, APOLLO_CACHE, TransferState],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
